'use client';
import Link from 'next/link';
import contentData from '@/lib/constants/content';
import { useCart } from '../../../components/commerce/cart/cart-context';
import { useAppPathname } from '@/lib/hooks/use-app-pathname';
import { useEffect, useRef } from 'react';

const SKIP_TARGETS = {
  MAIN: '#main',
  ADD_TO_CART: '#AddToCart'
};

export default function SkipLink() {
  const pathname = useAppPathname();
  const isProductRoute = pathname.includes('/products/');
  const isCartRoute = pathname === '/cart';
  const { cart } = useCart();
  const focusSentinelRef = useRef(null);

  // Reset focus to top of page on route changes
  useEffect(() => {
    // Focus a hidden element at the top so next Tab goes to skip links
    if (focusSentinelRef.current) {
      focusSentinelRef.current.focus();
    }
  }, [pathname]);

  return (
    <div>
      {/* Hidden focusable element to reset focus on route changes */}
      <div
        ref={focusSentinelRef}
        tabIndex={-1}
        className="sr-only"
        aria-hidden="true"
      />
      <div className="body-r sr-only flex max-w-xl flex-row justify-around focus-within:not-sr-only">
        <Link
          className="hover:text-cozy-cognac focus:text-cozy-cognac hover:underline focus:underline"
          href={SKIP_TARGETS.MAIN}
          onClick={(e) => skipToLocation({ e, location: SKIP_TARGETS.MAIN })}
          prefetch={false}
        >
          {contentData.skipLink.skipToContent['en-us']}
        </Link>
        {isProductRoute && (
          <Link
            className="hover:text-cozy-cognac focus:text-cozy-cognac hover:underline focus:underline"
            href={SKIP_TARGETS.ADD_TO_CART}
            onClick={(e) =>
              skipToLocation({ e, location: SKIP_TARGETS.ADD_TO_CART })
            }
            prefetch={false}
          >
            {contentData.skipLink.skipATCButton['en-us']}
          </Link>
        )}
        {!isCartRoute && (
          <Link
            href={'/cart'}
            prefetch={false}
            className="hover:text-cozy-cognac focus:text-cozy-cognac hover:underline focus:underline"
          >
            {contentData.skipLink.goToCart['en-us']}
          </Link>
        )}
        {cart?.totalQuantity > 0 && cart?.checkoutUrl && (
          <Link
            className="hover:text-cozy-cognac focus:text-cozy-cognac hover:underline focus:underline"
            prefetch={false}
            href={cart.checkoutUrl}
          >
            {contentData.skipLink.skipToCO['en-us']}
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Take over the default hash link behavior to ensure that the target is
 * not covered by the nav.
 */
function skipToLocation({ e, location }) {
  e.preventDefault();
  const target = document.querySelector(location);
  if (target) {
    // Using "focus" instead of normal hash link behavior actually scrolls to
    // the target in a natural way (i.e. without being covered by the nav).
    // Make sure the target is tabbable at least with tabIndex="-1".
    target.focus();

    // Fallback just in case the target isn't tabbable.
    if (document.activeElement !== target) {
      target.setAttribute('tabindex', '-1');
      target.focus();
    }
  } else {
    console.error(`No skip target found for location: ${location}`);
  }
}
